import { RouterState }      from "connected-react-router";
import React, {
  useEffect,
  useLayoutEffect,
  useState
}                           from 'react';
import {
  useDispatch,
  useSelector
}                           from "react-redux";
import BrowsePaneUpdater    from "../components/browsemode/BrowsePaneUpdater";
import TimelineComponent    from "../components/timeline/TimelineComponent";
import useSetTimelineValues from '../hooks/useSettimelineValues';
import slowDowner           from '../services/updater';

import { ApplicationState } from "../store";
import { BookmarkState }  from "../store/bookmarks/types";
import { BrowseState }    from "../store/browse/types";
import { LayoutState }    from "../store/layouts/types";
import { RecordingState } from "../store/recordings/types";
import { SettingsState }  from "../store/settings/types";
import {
  setPlaybackmode,
  setTimelineDate
}                         from "../store/timeline/actions";
import { TimelineState }  from "../store/timeline/types";
import { Routes }         from '../types';
import { chooseService }  from "../utils";

let shiftDate = 0;
let startPos: number | null = null;
let prevPos: number;
let startTime: number;
let startT: number;
let dir: number = 0;

const getWindowWidth = () => {
  return window.innerWidth
};

const getPower = (distance: number, time: number, duration: number): { power: number, friction: number } | null => {
  const speed = Math.abs(distance / time);
  if (speed < 0.7) return null;

  const sign = distance > 0 ? -1 : 1;
  return {
    power: sign * speed * duration / 50,    // adjust randomly - power of pushing timeline strip
    friction: duration / 5000               // adjust randomly - power of stopping timeline strip
  }
};

const TimelineContainer = () => {
  const {
    time,
    duration,
    scaleMarkInterval,
    scaleTimeMarkInterval,
    playbackMode
  } = useSelector<ApplicationState, TimelineState>(state => state.timeline);

  const {checkedPane, expandedVideoLayout} = useSelector<ApplicationState, LayoutState>(state => state.layouts);
  const {location} = useSelector<ApplicationState, RouterState>(state => state.router);
  const {startPoint, endPoint} = useSelector<ApplicationState, RecordingState>(state => state.recording);
  const {bookmarks} = useSelector<ApplicationState, BookmarkState>(state => state.bookmarks);
  const {browsePanes} = useSelector<ApplicationState, BrowseState>(state => state.browse);
  const {isLocalTime} = useSelector<ApplicationState, SettingsState>(state => state.settings);
  const [t, setT] = useState(time);
  const [width, setWidth] = useState(window.innerWidth);
  const dispatch = useDispatch();
  const panes = location.pathname === Routes.browse ? browsePanes : bookmarks;

  const {zoomInHandler, zoomOutHandler} = useSetTimelineValues();

  const exportBounds = startPoint
    ? chooseService(
      () => ({
        low: endPoint ? Math.min(startPoint, endPoint) : Math.min(startPoint, t),
        high: endPoint ? Math.max(startPoint, endPoint) : Math.max(startPoint, t)
      }),
      () => ({
        low: startPoint,
        high: startPoint
      })
    )
    : null;

  const timeStrips = Object.entries(panes)
    .filter(([pane]: any) => pane < 2)
    .reduce((hash: any, [pane, {timestrips}]: any) => {
      if (timestrips) {
        hash[pane] = timestrips;
      } else {
        hash[pane] = [];
      }
      return hash
    }, {});

  const camID = typeof checkedPane === 'number' && panes[checkedPane] && panes[checkedPane].id
    ? panes[checkedPane].id
    : null;

  let playbackCams = Object.entries(panes)
    .filter(([pane, {id}]: any) => id && pane < 2)
    .map(([pane, camID]: any) => camID.id)
    .filter(camId => camId);

  const timeOptions = {
    time: t,
    scaleMarkInterval,
    scaleTimeMarkInterval,
    duration,
    shiftDate,
    timestrips: timeStrips
  };

  useEffect(() => {
    setT(time);
    dir = time > t ? 1 : -1;
    shiftDate = 0;
  }, [time]);

  const onMouseDown = (e: React.MouseEvent) => {
    startTime = Date.now();
    startT = t;
    startPos = e.clientX;
    prevPos = e.clientX;
    shiftDate = 0;
    if (slowDowner.playing) stopPlaying();

    if (playbackMode === 'play' || playbackMode === 'back') {
      dispatch(setPlaybackmode('pause'))
    }
  };

  const stopPlaying = () => {
    slowDowner.stop();
    dispatch(setTimelineDate(slowDowner.time, 'pause'));
  };

  const onMouseMove = (e: React.MouseEvent, ref: React.RefObject<HTMLCanvasElement>) => {
    if (playbackMode !== 'pause') return;
    if (startPos === null || ref.current === null) return;
    shiftDate = (e.clientX - prevPos) / ref.current.width * duration;
    let newTime = Math.round(t - shiftDate);
    if (newTime < Date.now()) {
      prevPos = e.clientX;
      setT(newTime);
      dir = newTime > t ? 1 : -1;
    } else {
      shiftDate = 0
    }
  };

  const onMouseUp = (e: React.MouseEvent, ref: React.RefObject<HTMLCanvasElement>) => {
    if (!ref.current || startPos === null) return;

    const newTime = Math.round(t - shiftDate);
    dispatch(setTimelineDate(newTime));
    const slideOptions = getPower(e.clientX - startPos, Date.now() - startTime, duration);

    if (slideOptions) {
      const {power, friction} = slideOptions;
      slowDowner.onUpdate = () => {
        if (slowDowner.time > Date.now()) return stopPlaying();
        setT(slowDowner.time);
        dir = slowDowner.time > t ? 1 : -1;
      };
      slowDowner.onEnd = () => {
        dir = slowDowner.time > t ? 1 : -1;
        dispatch(setTimelineDate(slowDowner.time));
      };
      slowDowner.start(newTime, power, friction);
    }

    startPos = null;
  };

  const onWheeelMove = (e: any) => {
    const direction = e.deltaY > 0 ? 1 : -1;
    direction === -1 ? zoomInHandler() : zoomOutHandler();
  };

  useLayoutEffect(() => {
    const update = () => {
      setWidth(getWindowWidth);
    };

    window.addEventListener('resize', update);
    return () => window.removeEventListener('resize', update);
  }, []);


  return (
    <>
      <TimelineComponent
        timeOptions={timeOptions}
        onMouseDown={onMouseDown}
        onMouseMove={onMouseMove}
        onWheel={onWheeelMove}
        onMouseUp={onMouseUp}
        camID={camID}
        playbackCams={playbackCams}
        checkedPane={checkedPane}
        expandedVideoLayout={typeof expandedVideoLayout === 'number'}
        exportBounds={exportBounds}
        width={width}
        isLocalTime={isLocalTime}
      />
      <BrowsePaneUpdater time={timeOptions.time}
                         dir={dir}
      />
    </>
  )
};

export default TimelineContainer